<template>
  <v-dialog :value="true" persistent scrollable max-width="1100">
    <v-card :loading="carregando">
      <v-card-title class="text-h6">Registrar pagamento</v-card-title>
      <v-card-subtitle v-if="detalhe" class="pb-2">
        {{ nomeDoAluno }} · {{ detalhe.turma ? detalhe.turma.descricao : "- - -" }}
      </v-card-subtitle>
      <v-divider />

      <v-card-text v-if="parcela" class="pt-4">
        <e-tabela-parcela-pre-matricula
          :parcela="parcela"
          :faixas="detalhe.periodo ? detalhe.periodo.descontos : []"
          :valor-em-aberto="valorEmAberto"
          :matriculado="detalhe.matriculado_no_ano_anterior"
          class="mb-4"
        />

        <div class="d-flex flex-wrap">
          <v-currency-field v-model="form.juros" dense outlined class="mr-1" label="Juros" />
          <v-currency-field v-model="form.multa" dense outlined class="mx-1" label="Multa" />
          <v-currency-field v-model="form.desconto" dense outlined class="ml-1" label="Desconto" />
        </div>

        <div v-for="(pagamento, indice) in form.mensalidades" :key="indice" class="d-flex">
          <v-select
            v-model="pagamento.forma_de_pagamento_id"
            :items="formasDePagamento"
            :loading="carregandoFormas"
            item-text="descricao"
            item-value="id"
            label="Forma de Pagamento"
            dense
            outlined
            class="mr-1"
          />
          <v-currency-field
            v-model="pagamento.valor"
            dense
            outlined
            class="ml-1"
            label="Valor sendo pago"
          />
          <v-btn
            v-if="indice === form.mensalidades.length - 1"
            color="success"
            class="ml-2"
            aria-label="Adicionar forma de pagamento"
            :disabled="registrando"
            @click="adicionarPagamento"
          >
            <v-icon>fa fa-plus</v-icon>
          </v-btn>
          <v-btn
            v-else
            color="error"
            class="ml-2"
            aria-label="Remover forma de pagamento"
            @click="removerPagamento(indice)"
          >
            <v-icon>fa fa-close</v-icon>
          </v-btn>
        </div>

        <e-alerta-da-baixa
          :valor-sendo-pago="valorTotalSendoPago"
          :valor-em-aberto="valorEmAberto"
        />

        <v-alert v-if="parcela.descontoAplicavel" type="info" outlined dense class="mt-3 mb-0">
          Desconto da faixa vigente hoje ({{ parcela.descontoAplicavel | currency }}) já aplicado.
          Se o pagamento não tiver direito a ele, altere o campo Desconto.
        </v-alert>
        <v-alert v-if="parcela.linhaDigitavel" type="info" outlined dense class="mt-3 mb-0">
          O boleto será cancelado no Sicoob para não ser pago de novo.
        </v-alert>
      </v-card-text>

      <v-divider />
      <v-card-actions>
        <v-btn text :disabled="registrando" @click="$emit('fechar')">Cancelar</v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          :loading="registrando"
          :disabled="
            !parcela ||
            registrando ||
            valorTotalSendoPago <= 0 ||
            valorTotalSendoPago > valorEmAberto
          "
          @click="registrar"
        >
          Registrar pagamento
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Parcela from "@/Models/Parcela";
import EAlertaDaBaixa from "./EAlertaDaBaixa.vue";
import ETabelaParcelaPreMatricula from "./ETabelaParcelaPreMatricula.vue";
import { CODIGO_DO_BOLETO, mensagemDeErro } from "./helpers";

const novoPagamento = () => ({ forma_de_pagamento_id: 1, valor: 0 });

/**
 * Pagamento feito na escola, no mesmo formato da tela de baixa de parcela da mensalidade
 * (BaixarMensalidadePage): juros, multa e desconto informados pela secretaria e várias formas de
 * pagamento. Pagamento parcial é aceito e a pré-matrícula continua pendente. A API exige caixa
 * aberto e dá baixa no boleto do Sicoob.
 */
export default {
  name: "EModalRegistrarPagamento",
  components: { EAlertaDaBaixa, ETabelaParcelaPreMatricula },
  props: {
    preMatriculaId: { type: Number, required: true },
  },
  data() {
    return {
      detalhe: null,
      parcela: null,
      carregando: false,
      registrando: false,
      formasDePagamento: [],
      carregandoFormas: false,
      form: { juros: 0, multa: 0, desconto: 0, mensalidades: [novoPagamento()] },
    };
  },
  computed: {
    nomeDoAluno() {
      return this.detalhe.nome || (this.detalhe.aluno && this.detalhe.aluno.nomecompleto) || "";
    },
    // Mesma conta da BaixarMensalidadePage.
    valorEmAberto() {
      if (!this.parcela) return 0;
      return (
        this.parcela.valor -
        this.parcela.valorpago -
        this.form.desconto +
        this.form.juros +
        this.form.multa
      );
    },
    valorTotalSendoPago() {
      const total = this.form.mensalidades.reduce((soma, pagamento) => soma + pagamento.valor, 0);
      return parseFloat(total.toFixed(2) || 0);
    },
  },
  mounted() {
    this.carregar();
    this.carregarFormasDePagamento();
  },
  methods: {
    async carregar() {
      this.carregando = true;
      try {
        this.detalhe = await this.$services.preMatriculasService.pegar(this.preMatriculaId);
        this.parcela = new Parcela(this.detalhe.parcela);
        // O desconto da faixa vigente vem aplicado; a secretaria só mexe se não couber.
        this.form.desconto = this.parcela.descontoAplicavel || this.parcela.desconto || 0;
      } catch (error) {
        this.$toast.danger(mensagemDeErro(error));
        this.$emit("fechar");
      }
      this.carregando = false;
    },
    async carregarFormasDePagamento() {
      this.carregandoFormas = true;
      try {
        // A API já devolve só as ativas; o boleto pago chega pelo webhook, nunca pelo caixa.
        const formas = await this.$services.formasDePagamentoService.syncAll();
        this.formasDePagamento = formas.filter(
          (forma) => Number(forma.codigo) !== CODIGO_DO_BOLETO
        );
      } catch (error) {
        this.$toast.danger(mensagemDeErro(error));
      }
      this.carregandoFormas = false;
    },
    adicionarPagamento() {
      this.form.mensalidades.push(novoPagamento());
    },
    removerPagamento(indice) {
      this.form.mensalidades.splice(indice, 1);
    },
    async registrar() {
      this.registrando = true;
      try {
        const resposta = await this.$services.preMatriculasService.registrarPagamento(
          this.preMatriculaId,
          { ...this.form }
        );
        this.avisarResultado(resposta);
        this.$emit("registrado");
      } catch (error) {
        this.$toast.danger(mensagemDeErro(error));
      }
      this.registrando = false;
    },
    avisarResultado({ paga, boleto_baixado: boletoBaixado }) {
      if (!paga) {
        this.$toast.info("Pagamento parcial registrado. A pré-matrícula continua pendente.");
      } else if (boletoBaixado === false) {
        this.$toast.warn(
          "Pagamento registrado, mas não foi possível cancelar o boleto no Sicoob. Cancele-o manualmente."
        );
      } else if (boletoBaixado) {
        this.$toast.success("Pagamento registrado e boleto cancelado no Sicoob.");
      } else {
        this.$toast.success("Pagamento registrado.");
      }
    },
  },
};
</script>
